import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import {
  PiggyBank, Plus, TrendingUp, Clock, Trash2,
  Target, Sparkles, Calendar, ArrowLeft, Flame, Gift,
} from "lucide-react";

const HISTORY_PAGE_SIZE = 20;

export default function WalletPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [goals, setGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [streakData, setStreakData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Log savings form
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [goalId, setGoalId] = useState("");
  const [showForm, setShowForm] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, entryId: null });

  // Filter
  const [filterGoalId, setFilterGoalId] = useState("");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [userRes, goalsRes, summaryRes, historyRes, streakRes] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: "include" }),
        fetch(`${API}/goals/`, { credentials: "include" }),
        fetch(`${API}/savings/summary`, { credentials: "include" }),
        fetch(`${API}/savings/history?limit=${HISTORY_PAGE_SIZE + 1}`, { credentials: "include" }),
        fetch(`${API}/savings/streak-rewards`, { credentials: "include" }),
      ]);
      if (userRes.ok) setUser(await userRes.json());
      if (goalsRes.ok) setGoals((await goalsRes.json()).filter(g => g.status === "active"));
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (historyRes.ok) {
        const items = await historyRes.json();
        setHasMoreHistory(items.length > HISTORY_PAGE_SIZE);
        setHistory(items.slice(0, HISTORY_PAGE_SIZE));
        setHistoryOffset(HISTORY_PAGE_SIZE);
      }
      if (streakRes.ok) setStreakData(await streakRes.json());
    } catch (e) {
      console.error("Error:", e);
      toast.error("Failed to load wallet data. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreHistory = async () => {
    setIsLoadingMore(true);
    try {
      const res = await fetch(
        `${API}/savings/history?limit=${HISTORY_PAGE_SIZE + 1}&offset=${historyOffset}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to load more");
      const items = await res.json();
      setHasMoreHistory(items.length > HISTORY_PAGE_SIZE);
      setHistory(prev => [...prev, ...items.slice(0, HISTORY_PAGE_SIZE)]);
      setHistoryOffset(prev => prev + HISTORY_PAGE_SIZE);
    } catch (e) {
      toast.error("Failed to load more history.");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleLogSavings = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error("Enter a valid amount"); return; }
    setIsSaving(true);
    try {
      const res = await fetch(`${API}/savings/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount: val, note, goal_id: goalId || null }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      toast.success(`Logged $${val.toFixed(2)} saved!`);
      setAmount(""); setNote(""); setGoalId(""); setShowForm(false);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    setDeleteConfirm({ open: true, entryId });
  };

  const confirmDelete = async () => {
    const entryId = deleteConfirm.entryId;
    setDeleteConfirm({ open: false, entryId: null });
    try {
      const res = await fetch(`${API}/savings/log/${entryId}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete");
      toast.success("Entry removed");
      fetchData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });

  const thisMonth = new Date().getMonth();
  const thisYear = new Date().getFullYear();
  const monthSaved = history.filter(e => {
    const d = new Date(e.created_at);
    return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  }).reduce((sum, e) => sum + (e.amount ?? 0), 0);
  const weeksThisMonth = 4.33;
  const avgPerWeek = monthSaved > 0 ? monthSaved / weeksThisMonth : 0;

  const filteredHistory = filterGoalId
    ? history.filter(e => e.goal_id === filterGoalId)
    : history;

  const quickAmounts = [10, 25, 50, 100];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="wallet-skeleton">
        {/* Skeleton header */}
        <div className="bg-gradient-to-br from-sky-500 to-sky-600 px-6 pt-8 pb-12 rounded-b-3xl">
          <div className="h-4 w-24 bg-white/20 rounded animate-pulse mb-4" />
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 animate-pulse" />
            <div>
              <div className="h-3 w-20 bg-white/20 rounded animate-pulse mb-2" />
              <div className="h-8 w-32 bg-white/30 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="px-6 -mt-4 space-y-4">
          {/* Skeleton stat cards */}
          <div className="grid grid-cols-2 gap-3">
            {[1, 2].map(i => (
              <div key={i} className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="h-3 w-16 bg-slate-100 rounded animate-pulse mb-2" />
                <div className="h-6 w-20 bg-slate-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
          {/* Skeleton history rows */}
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-200 animate-pulse" />
              <div className="flex-1">
                <div className="h-4 w-24 bg-slate-200 rounded animate-pulse mb-1" />
                <div className="h-3 w-32 bg-slate-100 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="wallet-page">
      {/* Header */}
      <header className="bg-gradient-to-br from-sky-500 to-sky-600 text-white px-6 pt-8 pb-12 rounded-b-3xl">
        <button onClick={() => navigate("/dashboard")} className="text-sky-100 mb-4 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </button>
        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
            <PiggyBank className="w-7 h-7" />
          </div>
          <div>
            <p className="text-sky-100 text-sm">Total Saved</p>
            <p className="text-4xl font-bold font-accent" data-testid="total-saved">
              ${(summary?.total_saved || 0).toFixed(2)}
            </p>
          </div>
        </div>
        <div className="flex gap-4 mt-4">
          <div className="bg-white/10 rounded-xl px-4 py-2 flex-1 text-center">
            <p className="text-xl font-bold">${(summary?.this_week || 0).toFixed(0)}</p>
            <p className="text-xs text-sky-200">This Week</p>
          </div>
          <div className="bg-white/10 rounded-xl px-4 py-2 flex-1 text-center">
            <p className="text-xl font-bold">{summary?.total_entries || 0}</p>
            <p className="text-xs text-sky-200">Entries</p>
          </div>
          <div className="bg-white/10 rounded-xl px-4 py-2 flex-1 text-center">
            <p className="text-xl font-bold">${(summary?.avg_per_entry || 0).toFixed(0)}</p>
            <p className="text-xs text-sky-200">Avg Save</p>
          </div>
        </div>
      </header>

      <main className="px-6 -mt-4">
        {/* Monthly Summary */}
        <section className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Saved This Month</p>
            <p className="text-2xl font-bold text-emerald-600">${monthSaved.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Avg Per Week</p>
            <p className="text-2xl font-bold text-sky-600">${avgPerWeek.toFixed(2)}</p>
          </div>
        </section>

        {/* Log Savings Button / Form */}
        {!showForm ? (
          <Button
            onClick={() => setShowForm(true)}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl h-14 text-base font-bold shadow-lg shadow-emerald-500/20 mb-6"
            data-testid="log-savings-btn"
          >
            <Plus className="w-5 h-5 mr-2" /> Log Savings
          </Button>
        ) : (
          <section className="bg-white rounded-2xl border border-slate-100 p-5 mb-6 shadow-sm" data-testid="log-savings-form">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" /> Log Your Savings
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Track how much you saved today (in your bank, piggy jar, etc.)
            </p>

            {/* Quick amounts */}
            <div className="flex gap-2 mb-3">
              {quickAmounts.map((q) => (
                <button
                  key={q}
                  onClick={() => setAmount(q.toString())}
                  className={`flex-1 py-2.5 rounded-xl border-2 font-bold text-sm transition-all ${
                    amount === q.toString()
                      ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                      : "border-slate-100 text-slate-600 hover:border-slate-200"
                  }`}
                  data-testid={`quick-amount-${q}`}
                >
                  ${q}
                </button>
              ))}
            </div>

            {/* Custom amount */}
            <div className="relative mb-3">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
              <Input
                type="number"
                placeholder="Custom amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-8 rounded-xl h-11"
                data-testid="savings-amount-input"
              />
            </div>

            {/* Note */}
            <Input
              placeholder="What did you save for? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-xl h-11 mb-3"
              data-testid="savings-note-input"
            />

            {/* Link to goal */}
            {goals.length > 0 && (
              <select
                value={goalId}
                onChange={(e) => setGoalId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm mb-4"
                data-testid="savings-goal-select"
              >
                <option value="">Link to a goal (optional)</option>
                {goals.map((g) => (
                  <option key={g.goal_id} value={g.goal_id}>{g.title} — ${g.current_amount?.toFixed(0)}/{g.target_amount?.toFixed(0)}</option>
                ))}
              </select>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowForm(false); setAmount(""); setNote(""); setGoalId(""); }}
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLogSavings}
                disabled={isSaving || !amount}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl"
                data-testid="submit-savings-btn"
              >
                {isSaving ? "Saving..." : "Log Savings"}
              </Button>
            </div>
          </section>
        )}

        {/* Reward Credits (if any from referrals/refunds) */}
        {user?.wallet_balance > 0 && (
          <section className="bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200 rounded-2xl p-4 mb-6" data-testid="reward-credits">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-200 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-amber-700 font-medium">Reward Credits</p>
                  <p className="text-lg font-bold text-amber-900">${user.wallet_balance.toFixed(2)}</p>
                </div>
              </div>
              <p className="text-xs text-amber-600 max-w-[140px] text-right">From referrals & refunds. Use at checkout.</p>
            </div>
          </section>
        )}

        {/* Streak Rewards Progress */}
        {streakData && (
          <section className="bg-white rounded-2xl border border-slate-100 p-5 mb-6 shadow-sm" data-testid="streak-rewards">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" /> Savings Streak Rewards
            </h3>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-500">{streakData.current_streak}</p>
                <p className="text-xs text-slate-500">Day Streak</p>
              </div>
              <div className="flex-1">
                {streakData.milestones?.map((ms) => {
                  const reached = streakData.current_streak >= ms.days;
                  const progress = Math.min((streakData.current_streak / ms.days) * 100, 100);
                  return (
                    <div key={ms.days} className="mb-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className={reached ? "text-emerald-600 font-bold" : "text-slate-500"}>
                          {ms.days}-day streak
                        </span>
                        <span className={`font-bold ${reached ? "text-emerald-600" : "text-slate-400"}`}>
                          {reached ? "Earned!" : `+$${ms.credit.toFixed(2)}`}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${reached ? "bg-emerald-400" : "bg-orange-400"}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {streakData.next_milestone && (
              <p className="text-xs text-slate-500 text-center">
                <Gift className="w-3 h-3 inline mr-1" />
                {streakData.next_milestone.days_remaining} more day{streakData.next_milestone.days_remaining !== 1 ? "s" : ""} to earn ${streakData.next_milestone.credit.toFixed(2)} in reward credits!
              </p>
            )}
            {streakData.total_earned > 0 && (
              <p className="text-xs text-emerald-600 font-semibold text-center mt-2">
                Total earned from streaks: ${streakData.total_earned.toFixed(2)}
              </p>
            )}
          </section>
        )}

        {/* Savings History */}
        <section className="mb-24">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" /> Savings Log
            </h2>
            {goals.length > 0 && (
              <select
                value={filterGoalId}
                onChange={(e) => setFilterGoalId(e.target.value)}
                className="text-xs px-3 py-1.5 rounded-xl border border-slate-200 bg-white"
              >
                <option value="">All goals</option>
                {goals.map(g => (
                  <option key={g.goal_id} value={g.goal_id}>{g.title}</option>
                ))}
              </select>
            )}
          </div>

          {filteredHistory.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-slate-100">
              <PiggyBank className="w-14 h-14 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium mb-1">No savings logged yet</p>
              <p className="text-sm text-slate-400">Tap "Log Savings" to track your first entry</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredHistory.map((entry) => (
                <div
                  key={entry.entry_id}
                  className="bg-white rounded-xl p-4 flex items-center justify-between group"
                  data-testid={`savings-entry-${entry.entry_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">+${(entry.amount ?? 0).toFixed(2)}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(entry.created_at)}
                        {entry.note && <span>· {entry.note}</span>}
                      </div>
                      {entry.goal_id && (
                        <p className="text-xs text-sky-500 flex items-center gap-1 mt-0.5">
                          <Target className="w-3 h-3" /> Linked to goal
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(entry.entry_id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-rose-500 p-1"
                    data-testid={`delete-entry-${entry.entry_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {hasMoreHistory && (
                <Button
                  variant="outline"
                  className="w-full rounded-xl mt-2"
                  onClick={loadMoreHistory}
                  disabled={isLoadingMore}
                  data-testid="load-more-history"
                >
                  {isLoadingMore ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full border-2 border-sky-300 border-t-sky-600 animate-spin" />
                      Loading...
                    </span>
                  ) : "Load More"}
                </Button>
              )}
            </div>
          )}
        </section>
      </main>

      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, entryId: null })}>
        <DialogContent className="sm:max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle>Delete Entry</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mt-1">Remove this savings entry? This cannot be undone.</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setDeleteConfirm({ open: false, entryId: null })}>
              Cancel
            </Button>
            <Button className="flex-1 bg-red-500 hover:bg-red-600 rounded-full" onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
